<template>
  <v-flex
    id="lawyersPack"
    class="question"
  >
    <h2 class="display-3 display-3_dark">
      {{ getLawyersPackQuestions.label }}
    </h2>
    <div v-if="getLawyersPackQuestions.text">
      <p
        v-for="(text, index) in getLawyersPackQuestions.text"
        :key="index"
        class="mb-0 f-1"
      >
        {{ text }}
      </p>
    </div>
    <ul class="questions-list">
      <li
        v-if="getLawyersPackQuestions.hasOwnProperty('selected_law_cases')"
        class="mt-4"
      >
        <p
          class="header mb-1"
          :class="{ required: getLawyersPackQuestions.selected_law_cases.required }"
        >
          {{ getLawyersPackQuestions.selected_law_cases.label }}
        </p>
        <v-layout wrap>
          <v-flex
            v-for="(item, index) in lawCases"
            :key="index"
            md4
            sm6
            xs12
          >
            <v-checkbox
              v-model="selectedLawCases"
              v-validate="{ required: getLawyersPackQuestions.selected_law_cases.required }"
              multiple
              :label="item.description"
              :value="item.id"
              :error="errors.has('lawCases')"
              :error-messages="index + 1 === lawCases.length ? errors.collect('lawCases') : null"
              :data-vv-as="$t('label.question')"
              :data-vv-name="'lawCases'"
              :disabled="readOnly"
            />
          </v-flex>
        </v-layout>
        <div
          v-if="
            selectedLawCases.includes(82) &&
              getLawyersPackQuestions.hasOwnProperty('other_law_case')
          "
        >
          <h4 class="display-1 mb-2 mt-4 sz_dark_grey--text required">
            {{ getLawyersPackQuestions.other_law_case.label }}
          </h4>
          <v-textarea
            v-model="otherLawCase"
            v-validate="{
              ...requiredRegularField(getLawyersPackQuestions.other_law_case.required)
            }"
            :placeholder="$t('placeholders.add_text')"
            :error-messages="errors.collect('otherLawCase')"
            :data-vv-as="$t('label.question')"
            data-vv-name="otherLawCase"
            :required="getLawyersPackQuestions.other_law_case.required"
            class="small"
            :disabled="readOnly"
          />
        </div>
      </li>
      <li
        v-if="getLawyersPackQuestions.hasOwnProperty('selected_lawyer_services')"
        class="mt-4"
      >
        <p
          class="header mb-1"
          :class="{ required: getLawyersPackQuestions.selected_lawyer_services.required }"
        >
          {{ getLawyersPackQuestions.selected_lawyer_services.label }}
        </p>
        <v-radio-group
          v-model="selectedLawyerServices"
          v-validate="{ required: getLawyersPackQuestions.selected_lawyer_services.required }"
          :error-messages="errors.collect('selectedLawyerServices')"
          :data-vv-as="$t('label.question')"
          :data-vv-name="'selectedLawyerServices'"
          :disabled="readOnly"
        >
          <v-radio
            v-for="(item, index) in lawyerService"
            :key="index"
            off-icon=""
            on-icon=""
            :value="item.id"
            :label="item.description"
          />
        </v-radio-group>
      </li>
      <li
        v-if="getLawyersPackQuestions.hasOwnProperty('free_lawyer_help')"
        class="mt-4"
      >
        <p
          class="header"
          :class="{ required: getLawyersPackQuestions.free_lawyer_help.required }"
        >
          {{ getLawyersPackQuestions.free_lawyer_help.label }}
        </p>
        <ConfirmButtons
          v-model="freeLawyerHelp"
          v-validate="{ required: getLawyersPackQuestions.free_lawyer_help.required }"
          string
          :error-messages="errors.collect('freeLawyerHelp')"
          :data-vv-as="$t('label.question')"
          :data-vv-name="'freeLawyerHelp'"
          :disabled="readOnly"
        />
      </li>
      <li
        v-if="getLawyersPackQuestions.hasOwnProperty('paid_lawyer_help')"
        class="mt-4"
      >
        <p
          class="header"
          :class="{ required: getLawyersPackQuestions.paid_lawyer_help.required }"
        >
          {{ getLawyersPackQuestions.paid_lawyer_help.label }}
        </p>
        <ConfirmButtons
          v-model="paidLawyerHelp"
          v-validate="{ required: getLawyersPackQuestions.paid_lawyer_help.required }"
          string
          :error-messages="errors.collect('paidLawyerHelp')"
          :data-vv-as="$t('label.question')"
          :data-vv-name="'paidLawyerHelp'"
          :disabled="readOnly"
        />
      </li>
      <li
        v-if="isLawyerHelpReason && getLawyersPackQuestions.hasOwnProperty('lawyer_help_reason')"
        class="mt-4"
      >
        <p :class="{ required: getLawyersPackQuestions.lawyer_help_reason.required }">
          {{ getLawyersPackQuestions.lawyer_help_reason.label }}
        </p>
        <v-textarea
          v-model="lawyerHelpReason"
          v-validate="{ ...regularLongText(getLawyersPackQuestions.lawyer_help_reason.required) }"
          :placeholder="$t('placeholders.add_text')"
          :error-messages="errors.collect('lawyerHelpReason')"
          :data-vv-as="$t('label.question')"
          data-vv-name="lawyerHelpReason"
          auto-grow
          :disabled="readOnly"
        />
      </li>
      <li
        v-if="getLawyersPackQuestions.hasOwnProperty('law_problem_self_help')"
        class="mt-4"
      >
        <p
          class="header"
          :class="{ required: getLawyersPackQuestions.law_problem_self_help.required }"
        >
          {{ getLawyersPackQuestions.law_problem_self_help.label }}
        </p>
        <ConfirmButtons
          v-model="lawProblemSelfHelp"
          v-validate="{ required: getLawyersPackQuestions.law_problem_self_help.required }"
          string
          :error-messages="errors.collect('lawProblemSelfHelp')"
          :data-vv-as="$t('label.question')"
          :data-vv-name="'lawProblemSelfHelp'"
          :disabled="readOnly"
        />
      </li>
      <li
        v-if="
          lawProblemSelfHelp === 'yes' &&
            getLawyersPackQuestions.hasOwnProperty('law_problem_self_help_description')
        "
        class="mt-4"
      >
        <p
          class="header custom lA"
          :class="{ required: getLawyersPackQuestions.law_problem_self_help_description.required }"
        >
          {{ getLawyersPackQuestions.law_problem_self_help_description.label }}
        </p>
        <v-textarea
          v-model="lawProblemSelfHelpDescription"
          v-validate="{
            ...regularLongText(getLawyersPackQuestions.law_problem_self_help_description.required)
          }"
          :placeholder="$t('placeholders.add_text')"
          :error-messages="errors.collect('lawProblemSelfHelpDescription')"
          :data-vv-as="$t('label.question')"
          data-vv-name="lawProblemSelfHelpDescription"
          auto-grow
          :disabled="readOnly"
        />
      </li>
      <li
        v-if="getLawyersPackQuestions.hasOwnProperty('lawyer_help')"
        class="mt-4"
      >
        <p
          class="header mb-1"
          :class="{ required: getLawyersPackQuestions.lawyer_help.required }"
        >
          {{ getLawyersPackQuestions.lawyer_help.label }}
        </p>
        <ConfirmButtons
          v-model="lawyerHelp"
          v-validate="{ required: getLawyersPackQuestions.lawyer_help.required }"
          string
          :error-messages="errors.collect('lawyerHelp')"
          :data-vv-as="$t('label.question')"
          :data-vv-name="'lawyerHelp'"
          :disabled="readOnly"
        />
        <template v-if="lawyerHelp === 'yes'">
          <p
            v-for="(text, index) in getLawyersPackQuestions.lawyer_help.text"
            :key="index"
            class="f-14 mb-0 mt-1"
          >
            {{ text }}
          </p>
        </template>
      </li>
      <li
        v-if="lawyerHelp === 'yes' && getLawyersPackQuestions.hasOwnProperty('online_help_consent')"
        class="mt-4"
      >
        <p
          class="header"
          :class="{ required: getLawyersPackQuestions.online_help_consent.required }"
        >
          {{ getLawyersPackQuestions.online_help_consent.label }}
        </p>
        <ConfirmButtons
          v-model="onlineHelpConsent"
          v-validate="{ required: getLawyersPackQuestions.online_help_consent.required }"
          string
          :error-messages="errors.collect('lawyerOnlineHelpConsent')"
          :data-vv-as="$t('label.question')"
          :data-vv-name="'lawyerOnlineHelpConsent'"
          :disabled="readOnly"
        />
      </li>
    </ul>
  </v-flex>
</template>

<script>
import ConfirmButtons from '@/components/widgets/sheet/ConfirmButtons';
import sheetValidationRules from '@/mixins/sheet/sheetValidationRules';
import sheetResourcesGetter from '@/mixins/sheet/sheetResourcesGetter';

import { mapGetters } from 'vuex';
import { createHelpers } from 'vuex-map-fields';

const { mapFields } = createHelpers({
    getterType: 'sheet/getLawyersPackField',
    mutationType: 'sheet/updateLawyersPackField'
});

export default {
    name: 'LawyersPack',
    mixins: [sheetValidationRules, sheetResourcesGetter],
    inject: ['$validator'],
    props: {
        readOnly: {
            default: false,
            type: Boolean
        }
    },
    computed: {
        ...mapFields([
            'selectedLawCases',
            'otherLawCase',
            'selectedLawyerServices',
            'lawyerHelp',
            'freeLawyerHelp',
            'paidLawyerHelp',
            'lawyerHelpReason',
            'lawProblemSelfHelp',
            'lawProblemSelfHelpDescription',
            'onlineHelpConsent'
        ]),
        ...mapGetters('sheet', ['getLawyersPackQuestions']),
        lawCases () {
            return this.getResources(this.getLawyersPackQuestions.selected_law_cases);
        },
        lawyerService () {
            return this.getResources(this.getLawyersPackQuestions.selected_lawyer_services);
        },
        isLawyerHelpReason () {
            return this.paidLawyerHelp === 'yes' || this.freeLawyerHelp === 'yes';
        }
    },
    watch: {
        selectedLawCases (val) {
            if (!val.includes(82)) {
                this.otherLawCase = null;
            }
        },
        lawyerHelp: {
            handler (val) {
                if (val !== 'yes') {
                    this.onlineHelpConsent = null;
                }
            },
            immediate: true
        },
        isLawyerHelpReason (isLawyerHelpReason) {
            if (isLawyerHelpReason !== true) {
                this.lawyerHelpReason = null;
            }
        },
        lawProblemSelfHelp (lawProblemSelfHelp) {
            if (lawProblemSelfHelp !== 'yes') {
                this.lawProblemSelfHelpDescription = null;
            }
        }
    },
    components: { ConfirmButtons }
};
</script>

<style scoped></style>
